// src/components/AGTFit/food/FoodSearch.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Button,
  Menu,
  MenuItem,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  Scan,
  MoreHorizontal,
  Heart,
  X
} from 'lucide-react';
import nutritionixService from '../service/nutritionixService';
import fatsecretService from '../service/fatsecretService';
import agtfitService from '../service/agtfitService';
import { debounce } from 'lodash'; // Importamos lodash para usar debounce

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Componente de búsqueda estilizado
const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  borderRadius: 12,
  padding: '0 16px', // Usar valores exactos en lugar de theme.spacing
  marginBottom: 0, // Eliminar margen inferior
  width: '100%',
  height: 40, // Altura fija
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  padding: 0, // Eliminar padding
  '& .MuiInputBase-input': {
    padding: 0, // Eliminar padding interno
  }
}));

// Botón para escanear código de barras
const ScanButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '0 16px', // Usar valores exactos
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  boxShadow: 'none',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#E5E5E7'),
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center', // Centrar contenido
  gap: '4px', // Reducir espacio entre icono y texto
  elevation: 0,
  height: 40, // Altura fija
  minHeight: 40, // Asegurar altura mínima
  lineHeight: '40px', // Alinear texto verticalmente
  whiteSpace: 'nowrap', // Evita que el texto se rompa
}));

// Nuevo NavigationBar estilizado con ancho adaptable
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '350px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'),
}));

// StyledTab con ancho adaptable al contenido
const StyledTab = styled(Box)(({ theme, selected }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto',
  padding: '0 16px',
  borderRadius: '24px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...(selected && {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }),
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
  },
}));

// Tarjeta de alimento clickeable
const FoodItemCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#EAEAEC'),
  },
}));

// Función para redondear valores al entero más cercano
const roundNumber = (num) => {
  if (num === undefined || num === null) return 0;
  const decimal = num - Math.floor(num);
  return decimal >= 0.5 ? Math.ceil(num) : Math.floor(num);
};

// Función para normalizar los datos de alimentos
const normalizeFood = (food) => {
  // Si food es null o undefined, retornar un objeto vacío con valores por defecto
  if (!food) return {
    name: 'Unknown Food',
    calories: 0,
    protein: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
    sugar: 0,
    sodium: 0,
    servingSize: 1,
    servingUnit: 'serving',
    photo: null,
    foodId: null,
    brand_name: null,
    source: 'unknown'
  };

  // Valores por defecto si faltan datos
  const defaultMacros = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
    sugar: 0,
    sodium: 0
  };

  // Asegurarse de que todos los alimentos tengan los mismos campos
  return {
    name: food.food_name || food.name || 'Unknown Food',
    calories: food.nf_calories || food.calories || defaultMacros.calories,
    protein: food.nf_protein || food.protein || defaultMacros.protein,
    carbs: food.nf_total_carbohydrate || food.carbs || defaultMacros.carbs,
    fats: food.nf_total_fat || food.fats || defaultMacros.fats,
    fiber: food.nf_dietary_fiber || food.fiber || defaultMacros.fiber,
    sugar: food.nf_sugars || food.sugar || defaultMacros.sugar,
    sodium: food.nf_sodium || food.sodium || defaultMacros.sodium,
    servingSize: food.serving_qty || food.servingSize || 1,
    servingUnit: food.serving_unit || food.servingUnit || 'serving',
    photo: food.photo || null,
    foodId: food.foodId || food.food_id || null,
    brand_name: food.brand_name || null,
    source: food.source || 'nutritionix'
  };
};

const FoodSearch = ({ onSelectMeal, mealType, isModal = false, onClose, initialMode }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [createdFoods, setCreatedFoods] = useState([]);
  const [historyFoods, setHistoryFoods] = useState([]);
  const [, setSelectedFood] = useState(null);
  const [foodDetails, setFoodDetails] = useState(null);
  const [showFoodDetails, setShowFoodDetails] = useState(false);
  const [showCreateFoodModal, setShowCreateFoodModal] = useState(false);
  const [showCreateRecipeModal, setShowCreateRecipeModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [newFood, setNewFood] = useState({
    name: '',
    servingSize: '100',
    servingUnit: 'g',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    fiber: '',
    sugar: '',
    sodium: ''
  });
  
  // Referencias para los elementos de pestaña
  const tabsContainerRef = useRef(null);
  const tabRefs = useRef([]);
  if (tabRefs.current.length === 0) {
    tabRefs.current = Array(3).fill(null).map(() => ({ current: null }));
  }
  
  // Estado para el indicador de pestañas
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });

  // Crear función de búsqueda con debounce
  const debouncedSearch = useRef(
    debounce(async (searchText) => {
      if (!searchText.trim() || activeTab !== 0) return;
    
      setIsLoading(true);
      try {
        // Realizar búsquedas en paralelo en ambas APIs
        const [nutritionixPromise, fatsecretPromise] = [
          nutritionixService.searchFoods(searchText),
          fatsecretService.searchFoods(searchText, 0, 20)
        ];
    
        // Esperar a que ambas promesas se resuelvan
        const [nutritionixResponse, fatsecretResponse] = await Promise.allSettled([
          nutritionixPromise,
          fatsecretPromise
        ]);
    
        let combinedResults = [];
    
        // Procesar resultados de Nutritionix si la promesa se resolvió correctamente
        if (nutritionixResponse.status === 'fulfilled' && nutritionixResponse.value.success) {
          const nutritionixResults = nutritionixResponse.value.data.map(item => ({
            ...normalizeFood(item),
            source: 'nutritionix'
          }));
          combinedResults = [...combinedResults, ...nutritionixResults];
        }
    
        // Procesar resultados de FatSecret si la promesa se resolvió correctamente
        if (fatsecretResponse.status === 'fulfilled' && fatsecretResponse.value.success) {
          const fatsecretResults = fatsecretResponse.value.data.map(item => {
            // Extraer información nutricional básica de la descripción
            const description = item.food_description || '';
            let calories = 0;
    
            // Intentar extraer calorías de la descripción
            const caloriesMatch = description.match(/(\d+)\s*kcal/i);
            if (caloriesMatch && caloriesMatch[1]) {
              calories = parseInt(caloriesMatch[1], 10);
            }
    
            return {
              name: item.food_name,
              brand_name: item.brand_name || '',
              calories: calories,
              protein: 0, // Se obtendrá en los detalles
              carbs: 0,
              fats: 0,
              fiber: 0,
              sugar: 0,
              sodium: 0,
              servingSize: 1,
              servingUnit: 'serving',
              foodId: item.food_id,
              photo: { thumb: null },
              source: 'fatsecret'
            };
          });
    
          combinedResults = [...combinedResults, ...fatsecretResults];
        }
    
        // Ordenar resultados combinados por relevancia
        const searchLower = searchText.toLowerCase();
        combinedResults.sort((a, b) => {
          // Priorizar coincidencias exactas en el nombre
          const aExactMatch = a.name.toLowerCase() === searchLower;
          const bExactMatch = b.name.toLowerCase() === searchLower;
    
          if (aExactMatch && !bExactMatch) return -1;
          if (!aExactMatch && bExactMatch) return 1;
    
          // Priorizar alimentos con calorías conocidas
          if (a.calories && !b.calories) return -1;
          if (!a.calories && b.calories) return 1;
    
          // Ordenar alfabéticamente si todo lo demás es igual
          return a.name.localeCompare(b.name);
        });
    
        setSearchResults(combinedResults);
      } catch (error) {
        console.error('Error al buscar alimentos:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 500) // 500ms de debounce
  ).current;

  // Si se proporciona initialMode, configurar el componente
  useEffect(() => {
    if (initialMode === 'barcode') {
      handleScanBarcode();
    }
  }, [initialMode]);

  useEffect(() => {
    // Cargar historial y favoritos al montar el componente
    const loadUserFoods = async () => {
      try {
        const favResponse = await agtfitService.getFavoriteFoods();
        if (favResponse.success) {
          setFavorites(favResponse.data || []);
        }
        
        // Cargar historial de alimentos
        const historyResponse = await agtfitService.getFoodHistory();
        if (historyResponse.success) {
          setHistoryFoods(historyResponse.data || []);
        }
        
        // Cargar alimentos creados por el usuario
        const createdResponse = await agtfitService.getUserCreatedFoods();
        if (createdResponse.success) {
          setCreatedFoods(createdResponse.data || []);
        }
      } catch (error) {
        console.error('Error al cargar alimentos del usuario:', error);
      }
    };
    
    loadUserFoods();
  }, []);
  
  // Actualizar la posición del indicador cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (tabRefs.current[activeTab]?.current) {
        const tabElement = tabRefs.current[activeTab].current;
        const tabsContainer = tabsContainerRef.current;
        
        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;
          
          setIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };
    
    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeTab, tabRefs]);

  // Limpiar resultados de búsqueda al cambiar de pestaña
  useEffect(() => {
    if (activeTab !== 0) {
      setSearchResults([]);
    }
  }, [activeTab]);

  // Modificar el useEffect para usar la función con debounce
  useEffect(() => {
    if (searchText.trim() && activeTab === 0) {
      debouncedSearch(searchText);
    } else if (!searchText.trim()) {
      setSearchResults([]);
    }
    
    // Limpiar el debounce al desmontar
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchText, activeTab, debouncedSearch]);

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    // Limpiar el texto de búsqueda al cambiar de pestaña
    if (newValue !== 0) {
      setSearchText('');
    }
  };
  
  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };
  
  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };
  
  const handleMenuOption = (option) => {
    handleMoreMenuClose();
    
    switch(option) {
      case 'create-food':
        setShowCreateFoodModal(true);
        break;
      case 'create-recipe':
        setShowCreateRecipeModal(true);
        break;
      case 'manual-entry':
        setShowManualEntryModal(true);
        break;
      default:
        break;
    }
  };
  
  // Unused function - search is handled by debouncedSearch
  // const handleSearch = async () => {
  //   ... (removed unused duplicate search function)
  // };

  const handleFoodSelect = async (food) => {
    if (!food) return;
    
    const normalizedFood = normalizeFood(food);
    setSelectedFood(normalizedFood);
    setIsLoading(true);
    
    try {
      let response;
      const source = food.source || 'nutritionix'; // Por defecto usar Nutritionix
      
      if (source === 'nutritionix') {
        response = await nutritionixService.getFoodDetails(
          normalizedFood.name, 
          food.type || 'common'
        );
        
        if (response.success && response.data.foods && response.data.foods.length > 0) {
          const detailedFood = {
            ...normalizeFood(response.data.foods[0]),
            source: 'nutritionix'
          };
          setFoodDetails(detailedFood);
          setShowFoodDetails(true);
        } else {
          setFoodDetails({...normalizedFood, source: 'nutritionix'});
          setShowFoodDetails(true);
        }
      } else if (source === 'fatsecret') {
        // Para FatSecret, necesitamos el ID del alimento
        if (normalizedFood.foodId) {
          response = await fatsecretService.getFoodDetails(normalizedFood.foodId);
          
          if (response.success) {
            // Adaptar el formato de FatSecret al formato esperado
            const servings = response.data.servings.serving;
            const serving = Array.isArray(servings) ? servings[0] : servings;
            
            const adaptedFood = {
              name: response.data.food_name,
              brand_name: response.data.brand_name || '',
              calories: parseFloat(serving.calories) || 0,
              protein: parseFloat(serving.protein) || 0,
              carbs: parseFloat(serving.carbohydrate) || 0,
              fats: parseFloat(serving.fat) || 0,
              fiber: parseFloat(serving.fiber) || 0,
              sugar: parseFloat(serving.sugar) || 0,
              sodium: parseFloat(serving.sodium) || 0,
              servingSize: parseFloat(serving.metric_serving_amount) || 100,
              servingUnit: serving.metric_serving_unit || 'g',
              foodId: response.data.food_id,
              photo: { thumb: null },
              source: 'fatsecret'
            };
            
            setFoodDetails(adaptedFood);
            setShowFoodDetails(true);
          } else {
            setFoodDetails({...normalizedFood, source: 'fatsecret'});
            setShowFoodDetails(true);
          }
        } else {
          setFoodDetails({...normalizedFood, source: 'fatsecret'});
          setShowFoodDetails(true);
        }
      }
    } catch (error) {
      console.error('Error al obtener detalles del alimento:', error);
      setFoodDetails({...normalizedFood, source: food.source || 'nutritionix'});
      setShowFoodDetails(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddMeal = async (food, mealType) => {
    if (!food) return;
    
    try {
      const normalizedFood = normalizeFood(food);
      
      const mealData = {
        name: normalizedFood.name,
        type: mealType || 'snack',
        calories: normalizedFood.calories,
        protein: normalizedFood.protein,
        carbs: normalizedFood.carbs,
        fats: normalizedFood.fats,
        fiber: normalizedFood.fiber,
        sugar: normalizedFood.sugar,
        sodium: normalizedFood.sodium,
        servingSize: normalizedFood.servingSize,
        servingUnit: normalizedFood.servingUnit,
        date: new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString()
      };
      
      const response = await agtfitService.logMeal(mealData);
      
      if (response.success) {
        // Si hay una función de callback, llamarla
        if (onSelectMeal) {
          onSelectMeal(response.data);
        }
        
        // Si es un modal, cerrarlo
        if (isModal && onClose) {
          onClose();
        }
        
        // Actualizar historial
        setHistoryFoods(prev => [
          {
            ...mealData,
            time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
          },
          ...prev
        ]);
      }
    } catch (error) {
      console.error('Error al registrar comida:', error);
    }
  };
  
  const handleToggleFavorite = async (food, event) => {
    // Evitar propagación del evento si se proporciona
    if (event) {
      event.stopPropagation();
    }
    
    // Si food es null o undefined, no hacer nada
    if (!food) return;
    
    try {
      // Normalizar el alimento para asegurar consistencia
      const normalizedFood = normalizeFood(food);
      
      // Verificar si ya es favorito usando una comparación más precisa
      const isFavorite = isFavoriteFood(normalizedFood);
      
      if (isFavorite) {
        // Encontrar el ID exacto del alimento favorito
        const foodToRemove = favorites.find(f => 
          (f.foodId === normalizedFood.foodId) || 
          ((f.food_name === normalizedFood.name || f.name === normalizedFood.name) && normalizedFood.name)
        );
        
        if (foodToRemove) {
          const response = await agtfitService.removeFavoriteFood(foodToRemove.foodId || normalizedFood.foodId);
          if (response.success) {
            // Actualizar la lista de favoritos filtrando el elemento eliminado
            setFavorites(favorites.filter(f => 
              !(f.foodId === foodToRemove.foodId || 
                ((f.food_name === normalizedFood.name || f.name === normalizedFood.name) && normalizedFood.name))
            ));
          }
        }
      } else {
        // Añadir a favoritos
        const response = await agtfitService.addFavoriteFood({
          name: normalizedFood.name,
          calories: normalizedFood.calories,
          protein: normalizedFood.protein,
          carbs: normalizedFood.carbs,
          fats: normalizedFood.fats,
          fiber: normalizedFood.fiber,
          sugar: normalizedFood.sugar,
          sodium: normalizedFood.sodium
        });
        
        if (response.success) {
          // Añadir el nuevo favorito a la lista
          setFavorites([...favorites, { 
            ...normalizedFood, 
            foodId: response.data.foodId 
          }]);
        }
      }
    } catch (error) {
      console.error('Error al actualizar favoritos:', error);
    }
  };
  
  const handleScanBarcode = async () => {
    setShowBarcodeScanner(true);
    
    try {
      // En un entorno real, aquí se integraría con la cámara del dispositivo
      // Para este ejemplo, simularemos un escaneo con un código fijo
      const barcode = "049000042566"; // Ejemplo: Coca-Cola
      
      // Consultar ambas APIs en paralelo
      const [nutritionixPromise, fatsecretPromise] = [
        nutritionixService.scanBarcode(barcode),
        fatsecretService.scanBarcode(barcode)
      ];
      
      // Esperar a que ambas promesas se resuelvan
      const [nutritionixResponse, fatsecretResponse] = await Promise.allSettled([
        nutritionixPromise,
        fatsecretPromise
      ]);
      
      // Primero intentar con Nutritionix
      if (nutritionixResponse.status === 'fulfilled' && nutritionixResponse.value.success && 
          nutritionixResponse.value.data.foods && nutritionixResponse.value.data.foods.length > 0) {
        
        const normalizedFood = {
          ...normalizeFood(nutritionixResponse.value.data.foods[0]),
          source: 'nutritionix'
        };
        
        setSelectedFood(normalizedFood);
        setFoodDetails(normalizedFood);
        setShowFoodDetails(true);
      } 
      // Si Nutritionix no tiene resultados, intentar con FatSecret
      else if (fatsecretResponse.status === 'fulfilled' && fatsecretResponse.value.success && 
               fatsecretResponse.value.data) {
        
        // Adaptar el formato de FatSecret
        const servings = fatsecretResponse.value.data.servings.serving;
        const serving = Array.isArray(servings) ? servings[0] : servings;
        
        const normalizedFood = {
          name: fatsecretResponse.value.data.food_name,
          brand_name: fatsecretResponse.value.data.brand_name || '',
          calories: parseFloat(serving.calories) || 0,
          protein: parseFloat(serving.protein) || 0,
          carbs: parseFloat(serving.carbohydrate) || 0,
          fats: parseFloat(serving.fat) || 0,
          fiber: parseFloat(serving.fiber) || 0,
          sugar: parseFloat(serving.sugar) || 0,
          sodium: parseFloat(serving.sodium) || 0,
          servingSize: parseFloat(serving.metric_serving_amount) || 100,
          servingUnit: serving.metric_serving_unit || 'g',
          foodId: fatsecretResponse.value.data.food_id,
          photo: { thumb: null },
          source: 'fatsecret'
        };
        
        setSelectedFood(normalizedFood);
        setFoodDetails(normalizedFood);
        setShowFoodDetails(true);
      } else {
        // Fallback a datos simulados si ninguna API devuelve resultados
        const mockBarcodeResult = {
          food_name: "Greek Yogurt",
          brand_name: "Fage",
          serving_qty: 1,
          serving_unit: "container",
          serving_weight_grams: 170,
          nf_calories: 120,
          nf_total_fat: 4,
          nf_total_carbohydrate: 7,
          nf_protein: 15,
          nf_dietary_fiber: 0,
          nf_sugars: 7,
          nf_sodium: 65,
          photo: { thumb: "https://via.placeholder.com/80" },
          source: 'nutritionix'
        };
        
        const normalizedMock = normalizeFood(mockBarcodeResult);
        setSelectedFood(normalizedMock);
        setFoodDetails(normalizedMock);
        setShowFoodDetails(true);
      }
    } catch (error) {
      console.error('Error al escanear código de barras:', error);
    } finally {
      setShowBarcodeScanner(false);
    }
  };
  
  const handleCreateFood = async () => {
    try {
      // Validar datos
      if (!newFood.name || !newFood.calories) {
        alert("Por favor ingresa al menos el nombre y las calorías");
        return;
      }
      
      const foodData = {
        name: newFood.name,
        servingSize: parseFloat(newFood.servingSize) || 100,
        servingUnit: newFood.servingUnit || 'g',
        calories: parseFloat(newFood.calories) || 0,
        protein: parseFloat(newFood.protein) || 0,
        carbs: parseFloat(newFood.carbs) || 0,
        fats: parseFloat(newFood.fats) || 0,
        fiber: parseFloat(newFood.fiber) || 0,
        sugar: parseFloat(newFood.sugar) || 0,
        sodium: parseFloat(newFood.sodium) || 0
      };
      
      const response = await agtfitService.createCustomFood(foodData);
      
      if (response.success) {
        // Añadir a la lista de alimentos creados
        setCreatedFoods(prev => [normalizeFood(response.data), ...prev]);
        
        // Limpiar formulario y cerrar modal
        setNewFood({
          name: '',
          servingSize: '100',
          servingUnit: 'g',
          calories: '',
          protein: '',
          carbs: '',
          fats: '',
          fiber: '',
          sugar: '',
          sodium: ''
        });
        
        setShowCreateFoodModal(false);
      }
    } catch (error) {
      console.error('Error al crear alimento:', error);
    }
  };
  
  const handleManualEntry = async () => {
    try {
      // Validar datos
      if (!newFood.name || !newFood.calories) {
        alert("Por favor ingresa al menos el nombre y las calorías");
        return;
      }
      
      const mealData = {
        name: newFood.name,
        type: mealType || 'snack',
        servingSize: parseFloat(newFood.servingSize) || 100,
        servingUnit: newFood.servingUnit || 'g',
        calories: parseFloat(newFood.calories) || 0,
        protein: parseFloat(newFood.protein) || 0,
        carbs: parseFloat(newFood.carbs) || 0,
        fats: parseFloat(newFood.fats) || 0,
        fiber: parseFloat(newFood.fiber) || 0,
        sugar: parseFloat(newFood.sugar) || 0,
        sodium: parseFloat(newFood.sodium) || 0,
        date: new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString()
      };
      
      const response = await agtfitService.logMeal(mealData);
      
      if (response.success) {
        // Si hay una función de callback, llamarla
        if (onSelectMeal) {
          onSelectMeal(response.data);
        }
        
        // Limpiar formulario y cerrar modal
        setNewFood({
          name: '',
          servingSize: '100',
          servingUnit: 'g',
          calories: '',
          protein: '',
          carbs: '',
          fats: '',
          fiber: '',
          sugar: '',
          sodium: ''
        });
        
        setShowManualEntryModal(false);
        
        // Si es un modal, cerrarlo
        if (isModal && onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error al registrar entrada manual:', error);
    }
  };
  
  const displayedFoods = () => {
    // Mostrar resultados de búsqueda solo en la pestaña "All"
    if (activeTab === 0 && searchText.trim()) {
      return searchResults;
    }
    
    switch (activeTab) {
      case 0: // All - no mostrar nada si no hay búsqueda
        return [];
      case 1: // Like
        return favorites.map(normalizeFood);
      case 2: // Created
        return createdFoods.map(normalizeFood);
      default:
        return [];
    }
  };

  // Verificar si un alimento es favorito con comprobación de nulos
  const isFavoriteFood = (food) => {
    // Si food es null o undefined, retornar false
    if (!food) return false;
    
    return favorites.some(f => 
      (f.foodId === food.foodId) || 
      ((f.food_name === food.name || f.name === food.name) && food.name)
    );
  };

  return (
    <Box sx={{ 
      padding: isModal ? theme.spacing(3) : theme.spacing(3, 0),
      width: '100%'
    }}>
      {/* Barra de búsqueda con botones */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 3,
        flexWrap: 'nowrap', // Evita que los elementos se envuelvan en móviles
        height: 40 // Establecer altura fija para el contenedor principal
      }}>
        <SearchBox sx={{ flex: 1 }}>
          <Search size={20} color={theme.palette.text.secondary} />
          <SearchInput 
            placeholder="Search foods" 
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
          />
        </SearchBox>
        
        <ScanButton
          disableElevation
          onClick={handleScanBarcode}
          sx={{
            minWidth: theme.breakpoints.down('sm') ? 'auto' : undefined,
            px: theme.breakpoints.down('sm') ? 1.5 : 2,
            height: 40, // Forzar altura
            display: 'inline-flex', // Mejor control del layout
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Scan size={16} style={{marginRight: theme.breakpoints.down('sm') ? 0 : '4px'}} />
          {theme.breakpoints.down('sm') ? '' : 'Scan'}
        </ScanButton>
 
        <IconButton
          onClick={handleMoreMenuOpen}
          sx={{
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bgLight
              : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
            width: 40,
            height: 40,
            minHeight: 40, // Asegurar altura mínima
            padding: 0, // Eliminar padding
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px', // Mantener consistencia con otros elementos
            flexShrink: 0, // Evita que el botón se encoja
            '&:hover': {
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255,107,0,0.15)'
                : (theme.palette.mode === 'dark' ? '#3C3C3E' : '#E5E5E7'),
            }
          }}
        >
          <MoreHorizontal size={20} />
        </IconButton>
        
        <Menu
          anchorEl={moreMenuAnchorEl}
          open={Boolean(moreMenuAnchorEl)}
          onClose={handleMoreMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              minWidth: 180
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => handleMenuOption('create-food')}>
            <Typography variant="body1">Create Food</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuOption('create-recipe')}>
            <Typography variant="body1">Create Recipe</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMenuOption('manual-entry')}>
            <Typography variant="body1">Manual Entry</Typography>
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Navigation Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 4 }}>
        <StyledTabs ref={tabsContainerRef}>
          <TabIndicator style={indicatorStyle} />
          <StyledTab 
            ref={tabRefs[0]}
            onClick={() => handleTabChange(0)}
            selected={activeTab === 0}
          >
            All
          </StyledTab>
          <StyledTab 
            ref={tabRefs[1]}
            onClick={() => handleTabChange(1)}
            selected={activeTab === 1}
          >
            Like
          </StyledTab>
          <StyledTab 
            ref={tabRefs[2]}
            onClick={() => handleTabChange(2)}
            selected={activeTab === 2}
          >
            Created
          </StyledTab>
        </StyledTabs>
      </Box>
      
      {/* Resultados de búsqueda */}
      <Box sx={{ mb: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress color="warning" />
          </Box>
        ) : displayedFoods().length > 0 ? (
          displayedFoods().map((food, index) => {
            const normalizedFood = normalizeFood(food);
            const isFavorite = isFavoriteFood(normalizedFood);
            
            return (
              <FoodItemCard 
                key={index}
                onClick={() => handleFoodSelect(normalizedFood)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  {normalizedFood.photo?.thumb && (
                    <Avatar 
                      src={normalizedFood.photo.thumb} 
                      alt={normalizedFood.name}
                      sx={{ mr: 2, width: 40, height: 40 }}
                    />
                  )}
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {normalizedFood.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {roundNumber(normalizedFood.calories)} kcal • 
                      P: {roundNumber(normalizedFood.protein)}g • 
                      C: {roundNumber(normalizedFood.carbs)}g • 
                      F: {roundNumber(normalizedFood.fats)}g
                      {/* Mostrar la fuente como un pequeño badge */}
                      <Box 
                        component="span" 
                        sx={{ 
                          ml: 1,
                          fontSize: '0.7rem',
                          padding: '1px 4px',
                          borderRadius: '4px',
                          backgroundColor: normalizedFood.source === 'nutritionix' 
                            ? 'rgba(0, 123, 255, 0.1)' 
                            : 'rgba(255, 107, 0, 0.1)',
                          color: normalizedFood.source === 'nutritionix' 
                            ? '#007bff' 
                            : FIT_ORANGE
                        }}
                      >
                        {normalizedFood.source === 'nutritionix' ? 'NX' : 'FS'}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      color: isFavorite ? FIT_ORANGE : theme.palette.text.secondary,
                      mr: 1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(normalizedFood, e);
                    }}
                  >
                    <Heart size={18} fill={isFavorite ? FIT_ORANGE : 'none'} />
                  </IconButton>
                </Box>
              </FoodItemCard>
            );
          })
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {activeTab === 0 && searchText.trim() ? 'No foods found' : 
               activeTab === 1 && favorites.length === 0 ? 'No favorite foods yet' :
               activeTab === 2 && createdFoods.length === 0 ? 'No created foods yet' :
               activeTab === 0 ? 'Search for foods' : ''}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Sección de historial (solo visible en la pestaña "All" cuando no hay búsqueda) */}
      {activeTab === 0 && !searchText.trim() && historyFoods.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            History
          </Typography>
          
          <List sx={{ p: 0 }}>
            {historyFoods.map((food, index) => {
              const normalizedFood = normalizeFood(food);
              const isFavorite = isFavoriteFood(normalizedFood);
              
              return (
                <ListItem 
                  key={index}
                  sx={{ 
                    px: 0, 
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'}`,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleFoodSelect(normalizedFood)}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          color: isFavorite ? FIT_ORANGE : theme.palette.text.secondary,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(normalizedFood, e);
                        }}
                      >
                        <Heart size={18} fill={isFavorite ? FIT_ORANGE : 'none'} />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={normalizedFood.name}
                    secondary={`${roundNumber(normalizedFood.calories)} kcal • ${food.time || 'Today'}`}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      
      {/* Modal de detalles de alimento */}
      <Dialog 
        open={showFoodDetails} 
        onClose={() => setShowFoodDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">Food Details</Typography>
              {foodDetails && foodDetails.source && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    ml: 1, 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    backgroundColor: foodDetails.source === 'nutritionix' 
                      ? 'rgba(0, 123, 255, 0.1)' 
                      : 'rgba(255, 107, 0, 0.1)',
                    color: foodDetails.source === 'nutritionix' 
                      ? '#007bff' 
                      : FIT_ORANGE
                  }}
                >
                  {foodDetails.source === 'nutritionix' ? 'Nutritionix' : 'FatSecret'}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setShowFoodDetails(false)}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {foodDetails && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {foodDetails.photo?.thumb && (
                  <Avatar 
                    src={foodDetails.photo.thumb} 
                    alt={foodDetails.name}
                    sx={{ mr: 2, width: 60, height: 60 }}
                  />
                )}
                <Box>
                  <Typography variant="h6">
                    {foodDetails.name}
                  </Typography>
                  {foodDetails.brand_name && (
                    <Typography variant="body2" color="text.secondary">
                      {foodDetails.brand_name}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Typography variant="subtitle1" gutterBottom>
                Nutritional Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Serving Size:</strong> {foodDetails.servingSize} {foodDetails.servingUnit || 'g'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Calories:</strong> {roundNumber(foodDetails.calories)} kcal
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Protein:</strong> {roundNumber(foodDetails.protein)} g
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Carbs:</strong> {roundNumber(foodDetails.carbs)} g
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Fat:</strong> {roundNumber(foodDetails.fats)} g
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Fiber:</strong> {roundNumber(foodDetails.fiber)} g
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Sugar:</strong> {roundNumber(foodDetails.sugar)} g
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Sodium:</strong> {roundNumber(foodDetails.sodium)} mg
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={(e) => handleToggleFavorite(foodDetails, e)}
            startIcon={<Heart size={18} fill={isFavoriteFood(foodDetails) ? FIT_ORANGE : 'none'} />}
            sx={{ color: theme.palette.text.primary }}
          >
            {isFavoriteFood(foodDetails) ? 'Remove from favorites' : 'Add to favorites'}
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
              color: '#fff' 
            }}
            onClick={() => handleAddMeal(foodDetails, mealType)}
          >
            Add to {mealType || 'meal'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de creación de alimento */}
      <Dialog 
        open={showCreateFoodModal} 
        onClose={() => setShowCreateFoodModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create Food</Typography>
            <IconButton onClick={() => setShowCreateFoodModal(false)}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Food Name"
                fullWidth
                value={newFood.name}
                onChange={(e) => setNewFood({...newFood, name: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Serving Size"
                type="number"
                fullWidth
                value={newFood.servingSize}
                onChange={(e) => setNewFood({...newFood, servingSize: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                fullWidth
                value={newFood.servingUnit}
                onChange={(e) => setNewFood({...newFood, servingUnit: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Calories"
                type="number"
                fullWidth
                value={newFood.calories}
                onChange={(e) => setNewFood({...newFood, calories: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Protein (g)"
                type="number"
                fullWidth
                value={newFood.protein}
                onChange={(e) => setNewFood({...newFood, protein: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Carbs (g)"
                type="number"
                fullWidth
                value={newFood.carbs}
                onChange={(e) => setNewFood({...newFood, carbs: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fats (g)"
                type="number"
                fullWidth
                value={newFood.fats}
                onChange={(e) => setNewFood({...newFood, fats: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fiber (g)"
                type="number"
                fullWidth
                value={newFood.fiber}
                onChange={(e) => setNewFood({...newFood, fiber: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sugar (g)"
                type="number"
                fullWidth
                value={newFood.sugar}
                onChange={(e) => setNewFood({...newFood, sugar: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Sodium (mg)"
                type="number"
                fullWidth
                value={newFood.sodium}
                onChange={(e) => setNewFood({...newFood, sodium: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowCreateFoodModal(false)}
            sx={{ color: theme.palette.text.primary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
              color: '#fff' 
            }}
            onClick={handleCreateFood}
          >
            Create Food
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de entrada manual */}
      <Dialog 
        open={showManualEntryModal} 
        onClose={() => setShowManualEntryModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manual Entry</Typography>
            <IconButton onClick={() => setShowManualEntryModal(false)}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Food Name"
                fullWidth
                value={newFood.name}
                onChange={(e) => setNewFood({...newFood, name: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Serving Size"
                type="number"
                fullWidth
                value={newFood.servingSize}
                onChange={(e) => setNewFood({...newFood, servingSize: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                fullWidth
                value={newFood.servingUnit}
                onChange={(e) => setNewFood({...newFood, servingUnit: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Calories"
                type="number"
                fullWidth
                value={newFood.calories}
                onChange={(e) => setNewFood({...newFood, calories: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Protein (g)"
                type="number"
                fullWidth
                value={newFood.protein}
                onChange={(e) => setNewFood({...newFood, protein: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Carbs (g)"
                type="number"
                fullWidth
                value={newFood.carbs}
                onChange={(e) => setNewFood({...newFood, carbs: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fats (g)"
                type="number"
                fullWidth
                value={newFood.fats}
                onChange={(e) => setNewFood({...newFood, fats: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fiber (g)"
                type="number"
                fullWidth
                value={newFood.fiber}
                onChange={(e) => setNewFood({...newFood, fiber: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sugar (g)"
                type="number"
                fullWidth
                value={newFood.sugar}
                onChange={(e) => setNewFood({...newFood, sugar: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Sodium (mg)"
                type="number"
                fullWidth
                value={newFood.sodium}
                onChange={(e) => setNewFood({...newFood, sodium: e.target.value})}
                margin="normal"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowManualEntryModal(false)}
            sx={{ color: theme.palette.text.primary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            sx={{ 
              background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
              color: '#fff' 
            }}
            onClick={handleManualEntry}
          >
            Add to {mealType || 'meal'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de creación de receta */}
      <Dialog 
        open={showCreateRecipeModal} 
        onClose={() => setShowCreateRecipeModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create Recipe</Typography>
            <IconButton onClick={() => setShowCreateRecipeModal(false)}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" paragraph>
            Recipe creation feature coming soon.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowCreateRecipeModal(false)}
            sx={{ color: theme.palette.text.primary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de escáner de código de barras */}
      <Dialog 
        open={showBarcodeScanner} 
        onClose={() => setShowBarcodeScanner(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Scan Barcode</Typography>
            <IconButton onClick={() => setShowBarcodeScanner(false)}>
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            height: 300, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <Box sx={{ 
              border: '2px dashed #ccc', 
              borderRadius: 2, 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 2
            }}>
              <CircularProgress color="warning" />
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              Scanning barcode...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FoodSearch;